import { useEffect, useRef, useState } from 'react';

import { SocketConnection } from '@/connection/socket/socket-main-thread/socket-connection';
import socketManager from '@/connection/socket/socket-main-thread/socket-manager';
import { updateProjectSettings, addLeaderboardNavItem } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { EventSettings } from '@/types/projects.types';
import { NavMainItems } from '@/types/homepage.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import toastTrigger from '@ui/toast/toast-trigger';
import { PointsEditModal } from './points-edit-modal';
import LeaderboardActionSettings from './action-settings';
import LeaderboardCSVUpload from './csv-upload';
import Modal from '@ui/modal/modal';
import { checkRegistrationToggled } from '@/utils/registration/check-registration-toggled';
import { getActiveChannel, getUser } from '@/store/slices/user/selectors';
import { getIsLoadingWorkingProject, getWorkingProject } from '@/store/slices/project/selectors';
import {
  getLeaderboardEnabled,
  getLeaderboardLoadingActions,
  getLeaderboardMainActions,
} from '@/store/slices/leaderboard/selectors';
import { loadActionPoints } from '@/store/slices/leaderboard';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import { EToastContainerIds, EToastTypes } from '@ui/toast/toast.types';

import './action-settings.css';
import Text from '@ui/text/text/text';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import {
  TGlobalContainerDesigns,
  TGlobalIcoTextDesigns,
} from '@styles/design-system/global-components/global-components.types';
import Switch from '@ui/switch/switch';
import GradientLoader from '@ui/loader/gradient-loader';

const Leaderboard = () => {
  const user = useAppSelector(getUser);
  const active_channel = useAppSelector(getActiveChannel);
  const workingEvent = useAppSelector(getWorkingProject);
  const loadingEvent = useAppSelector(getIsLoadingWorkingProject);
  const leaderboardEnabled = !!useAppSelector(getLeaderboardEnabled);
  const loadingActions = useAppSelector(getLeaderboardLoadingActions);
  const currentActions = useAppSelector(getLeaderboardMainActions);
  const [toggleEditPointModal, setToggleEditPointModal] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const socket = useRef<SocketConnection | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const _event = workingEvent?.event ?? workingEvent?.event;
    if (_event && user) {
      dispatch(loadActionPoints({ channel: user.active_channel, eventId: _event }));
    }
  }, [dispatch, user, workingEvent]);

  useEffect(() => {
    const uploadComplete = () => {
      toastTrigger('Upload complete', {
        description: 'Your leaderboard custom actions upload has finished processing',
        type: EToastTypes.success,
      });
    };

    const invalidCSV = () => {
      toastTrigger('Invalid File Uploaded', {
        description: 'The file was uploaded in an invalid format. Please upload a file that is formatted correctly.',
        type: EToastTypes.error,
      });
    };

    const uploadFailure = () => {
      toastTrigger('Error', {
        description: 'There was an error processing your upload',
        type: EToastTypes.error,
      });
    };

    if (active_channel) {
      socket.current = socketManager.get(`admin-${active_channel}`);
      socket.current.addListener('leaderboard.complete', uploadComplete);
      socket.current.addListener('leaderboard.error.invalid', invalidCSV);
      socket.current.addListener('leaderboard.error.internal', uploadFailure);
    }

    return () => {
      socket.current?.removeListener('leaderboard.complete', uploadComplete);
      socket.current?.removeListener('leaderboard.error.invalid', invalidCSV);
      socket.current?.removeListener('leaderboard.error.internal', uploadFailure);
    };
  }, [active_channel, socket]);

  const handleToggleLeaderboard = (_: unknown, on: boolean) => {
    if (!on) {
      setConfirmationModalOpen(true);
    } else {
      toggleLeaderboard(on);
    }
  };

  const toggleLeaderboard = (on: boolean) => {
    if (workingEvent) {
      // registration required for leaderboard feature
      if (on) {
        if (!workingEvent.registration_on) {
          checkRegistrationToggled(true, workingEvent, dispatch);
          toastTrigger('Registration Turned On', {
            description:
              'Enabling the Leaderboard feature requires attendees to register, including first and last name.',
            type: EToastTypes.info,
            containerId: EToastContainerIds.stacked,
          });
        }

        toastTrigger('Leaderboard feature enabled', {
          description:
            'Points will not apply retroactively for previously completed actions. A CSV upload can be used to update attendee scores manually.',
          type: EToastTypes.info,
          containerId: EToastContainerIds.stacked,
        });
      }
      const eventSettings: EventSettings = {
        ...workingEvent.settings,
        leaderboard_settings: {
          ...workingEvent.settings.leaderboard_settings,
          enabled: on,
        },
      };
      dispatch(updateProjectSettings({ event_uuid: workingEvent.uuid, settings: eventSettings }));

      // create a leaderboard main nav item if it doesn't already exist and leaderboard tracking is turned on
      if (workingEvent.homepage?.event_main_nav_items) {
        const hasLeaderboardNavItem = workingEvent.homepage.event_main_nav_items.find(
          (ni) => ni.name === NavMainItems.Leaderboard
        );
        if (!hasLeaderboardNavItem && on) {
          const homepageId = workingEvent.homepage.homepage;
          dispatch(addLeaderboardNavItem({ eventUuid: workingEvent.uuid, homepageId }));
        }
      }
    }
  };

  const next = () => {
    setConfirmationModalOpen(false);
    toggleLeaderboard(false);
  };

  const handleEditActions = () => {
    // to prevent component re-renders/flashing we take loadingActions out of the wrapper conditional and move it here
    // however, since we are still loading the actions data, we do not want to allow the edit modal to be opened since we don't yet
    // have the data
    if (loadingActions) return;

    setToggleEditPointModal(true);
  };

  return (
    <>
      <Container design={TGlobalContainerDesigns.GlobalCont1} borderRadius="12px">
        <Container
          borderRadius="12px"
          padding="16px"
          flex="1"
          width="100%"
          design={TGlobalContainerDesigns.GlobalCont1}
          className="flex jc-space-between ai-center"
          gap="16px"
        >
          <Container className="flex flex-column gap-4" width="100%">
            <Text design={TGlobalIcoTextDesigns.GlobalIcoText1} className="H6" tag="h6">
              Leaderboard
            </Text>
            <Text design={TGlobalIcoTextDesigns.GlobalIcoText3} className="C2">
              Measure user engagement by assigning point values to actions or uploading custom points
            </Text>
          </Container>
          <Switch on={leaderboardEnabled} onClick={handleToggleLeaderboard} value={'leaderboard'} />
        </Container>
        <OptionalComponent display={leaderboardEnabled}>
          <div style={{ height: '1px', background: 'var(--gray-700)', margin: '0 16px' }} />
          <Container padding="16px" className="flex flex-column gap-16">
            {!loadingEvent ? (
              <LeaderboardActionSettings
                actions={currentActions}
                editActions={handleEditActions}
                loadingActions={loadingActions}
              />
            ) : (
              <div className="loading-action-container">
                <GradientLoader />
              </div>
            )}

            {!loadingEvent && <LeaderboardCSVUpload />}
          </Container>
        </OptionalComponent>
      </Container>

      {!loadingEvent && (
        <PointsEditModal
          open={toggleEditPointModal}
          closeFn={setToggleEditPointModal}
          initialActions={currentActions}
        />
      )}
      <Modal
        open={confirmationModalOpen}
        customSize={{
          width: '600px',
          height: 'auto',
          maxHeight: '300px',
          minHeight: '200px',
          maxWidth: 'calc(100vw - 80px)',
        }}
        contentWrapperClassName="padding-h-40 padding-v-24 gap-40"
        onRequestClose={() => setConfirmationModalOpen(false)}
        RenderHeaderContent={() => (
          <Container className="radius-10">
            <Text tag="h3" color="var(--gray-100)" className="H3 margin-b-8">
              Disable Leaderboard
            </Text>
          </Container>
        )}
        RenderFooterContent={() => (
          <Container borderRadius="0 0 12px 12px" className="flex fd-row jc-flex-end ai-center gap-16">
            <Button design="LocalBttnSecHalfTransp" designSize="large" onClick={() => setConfirmationModalOpen(false)}>
              <Text className="L1">Cancel</Text>
            </Button>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              designSize="large"
              onClick={next}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Continue
              </Text>
            </Button>
          </Container>
        )}
      >
        <Container className="overflow-auto" height="100%">
          <Text tag="p" color="var(--gray-300)" className="P1">
            Are you sure you want to disable the Leaderboard feature?
          </Text>
        </Container>
      </Modal>
    </>
  );
};

export default Leaderboard;
